import React from 'react'
import { createPortal } from 'react-dom'
import * as ReactJsxDevRuntime from 'react/jsx-dev-runtime'
import * as ReactJsxRuntime from 'react/jsx-runtime'
import { getFunctions, httpsCallable } from 'firebase/functions'
import styled, * as StyledComponents from 'styled-components'
import { firebaseApp } from '../services'
import type { SurveyCallableFunctionName } from '../survey-tools/callable-functions'

export const SURVEY_RUNTIME_VERSION = '1' as const

export interface SurveyRuntimeV1 {
  version: typeof SURVEY_RUNTIME_VERSION
  React: typeof React
  callFunction: <Request, Response>(
    name: SurveyCallableFunctionName,
    data: Request,
  ) => Promise<Response>
  createPortal: typeof createPortal
  jsxRuntime: typeof ReactJsxRuntime
  jsxDevRuntime: typeof ReactJsxDevRuntime
  styled: typeof styled
  styledComponents: typeof StyledComponents
}

const allowedSurveyCallables = new Set<SurveyCallableFunctionName>([
  'categoriseText',
  'generateReport',
])

async function callFunction<Request, Response>(
  name: SurveyCallableFunctionName,
  data: Request,
): Promise<Response> {
  if (!allowedSurveyCallables.has(name)) {
    throw new Error(`Unsupported survey callable function ${name}`)
  }

  const callable = httpsCallable<Request, Response>(getFunctions(firebaseApp), name)
  return (await callable(data)).data
}

declare global {
  // Survey bundles use this object to share the shell's React and styled-components singletons.
  // eslint-disable-next-line no-var
  var __CONFER_SURVEY_RUNTIME_V1__: SurveyRuntimeV1 | undefined
}

export function ensureSurveyRuntimeV1(): SurveyRuntimeV1 {
  const existingRuntime = globalThis.__CONFER_SURVEY_RUNTIME_V1__
  if (existingRuntime) {
    if (existingRuntime.version !== SURVEY_RUNTIME_VERSION) {
      throw new Error(`Unsupported Confer survey runtime ${existingRuntime.version}`)
    }
    return existingRuntime
  }

  const runtime: SurveyRuntimeV1 = {
    version: SURVEY_RUNTIME_VERSION,
    React,
    callFunction,
    createPortal,
    jsxRuntime: ReactJsxRuntime,
    jsxDevRuntime: ReactJsxDevRuntime,
    styled,
    styledComponents: StyledComponents,
  }

  globalThis.__CONFER_SURVEY_RUNTIME_V1__ = runtime
  return runtime
}
